import { isDenialReason } from "../agent-start-failure.js";
import { channelState, dmState } from "../core/state.svelte.js";
import { cyboState } from "../plugins/agents/state.svelte.js";
import { reportUiFailure, shouldReport } from "../report-failure.js";
import {
  type ReportSubject,
  channelSubject,
  dmPeerSubject,
  workspaceSubject,
} from "../report-subject.js";

// A `cyborg:error` with no pending request to reject; older frames name their reason in `error`.
export interface ServerErrorFrame {
  code?: unknown;
  error?: unknown;
  message: string;
  requestId?: string;
  clientMsgId?: string;
  recorded?: boolean;
}

// Unkeyed frames repeat (the relay rate limit, per keystroke): once a minute per code.
const UNKEYED_REFUSAL_INTERVAL_MS = 60_000;

const WIRE_CODE = /^[a-z][a-z0-9_]{0,63}$/;

function wireCodeOf(value: unknown): string | undefined {
  return typeof value === "string" && WIRE_CODE.test(value) ? value : undefined;
}

function sendTarget(clientMsgId: string | undefined): ReportSubject {
  if (!clientMsgId) return {};
  const inChannel = channelState.messages.find((m) => m.clientMsgId === clientMsgId);
  if (inChannel?.channelId) return channelSubject(inChannel.channelId);
  const inDm = dmState.messages.find((m) => m.clientMsgId === clientMsgId);
  return inDm?.toId ? dmPeerSubject(inDm.toId, cyboState.list) : {};
}

// The only sink for fire-and-forget refusals (channel/DM send, permission response).
// Code and ids only: the server's prose can quote a channel or a person.
export function reportServerErrorFrame(
  frame: ServerErrorFrame,
  workspaceId: string | undefined,
): void {
  // The relay already reported this failure; a second report would double-count it.
  if (frame.recorded === true) return;
  const code = wireCodeOf(frame.code) ?? wireCodeOf(frame.error) ?? "unknown";
  const summary = { code, message: `cyborg:error ${code}` };
  const ids = {
    clientMsgId: frame.clientMsgId,
    requestId: frame.requestId,
    ...workspaceSubject(workspaceId),
    ...sendTarget(frame.clientMsgId),
  };
  // The relay's authorization-denial families (classifyDenialReason) are refusals too.
  const options = isDenialReason(code) ? { failureKind: "refusal" as const } : {};
  if (frame.clientMsgId) {
    reportUiFailure("chat.server_refusal", summary, ids, options);
    return;
  }
  if (shouldReport(`chat.server_refusal:${code}`, UNKEYED_REFUSAL_INTERVAL_MS)) {
    reportUiFailure("chat.server_refusal", summary, ids, options);
  }
}
